import Overlay from 'components/ui/Overlay/Overlay';
import { ServiceTypes } from 'constants/ServiceTypes';
import { SessionType } from 'enums/SessionType';
import { History } from 'history';
import { ISessionUpdateRequest } from 'interfaces/review/session/Session';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { useInjection } from 'services/ioc/ioc.react';
import { GameReviewStore } from 'stores/GameReviewStore';
import NoteStore from 'stores/NoteStore';
import { SessionStore } from 'stores/SessionStore';
import { v1 as uuid } from 'uuid';
import { SessionReviewRowManager } from '../../models/review/SessionReviewRowManager';
import EditPlaysGrid from './Components/EditPlaysGrid';
import { MissingOwningOrgModal } from './Components/MissingOwningOrgModal/MissingOwningOrgModal';
import SubmitModal from './Components/SubmitModal/SubmitModal';
import TopBar from './Components/TopBar/TopBar';

interface IProps {
  match?: any;
  history: History;
}
const SessionReview: React.FC<IProps> = observer((props): React.ReactElement => {
  const rowManagerRef = useRef<SessionReviewRowManager>(new SessionReviewRowManager());
  const reviewStore = useInjection<GameReviewStore>(ServiceTypes.GameReviewStore);
  const { match, history } = props;
  const { id } = match.params;
  const [shouldUpdate, setShouldUpdate] = useState<string>('');
  const [isDisplayingWarmups, setIsDisplayingWarmups] = useState<boolean>(rowManagerRef.current.isDisplayingWarmups);
  const rowManager = rowManagerRef.current;
  const noteStore = useInjection<NoteStore>(ServiceTypes.NoteStore);
  const [isSettingsModalVisible, setSettingsModalVisible] = React.useState(false);
  const [missingOrgModalVisible, setMissingOrgModalVisible] = useState<boolean>(false);
  const [owningOrgId, setOwningOrgId] = useState<string | undefined>();
  const [sessionSettingsChanged, setSessionSettingsChanged] = useState<boolean>(false);

  useEffect(() => {
    window.onpopstate = () => {
      history.replace('/');
    };
  }, [window]);

  useEffect(() => {
    initializeData();
    return () => {
      reviewStore.setIsReviewing(false);
    };
  }, []);

  function getRows() {
    return rowManager.rows;
  }

  const initializeData = async () => {
    reviewStore.setIsReviewing(true);
    // setScrollBarWidth(getScrollBarWidth());
    await reviewStore.setReviewGame(id);
    const { sessionMetadata, sessionId } = reviewStore.reviewGame;
    setOwningOrgId(sessionMetadata.owningOrganizationId);
    if (sessionId && sessionMetadata.sessionType === SessionType.BattingPractice) {
      history.push(`/review-bp/${sessionId}`);
    }
    rowManager.onGridUpdate = () => {
      setShouldUpdate(uuid());
    };
  };

  const onToggleWarmupsChanged = (newVal: boolean) => {
    setIsDisplayingWarmups(newVal);
    rowManager.setIsDisplayingWarmups(newVal);
  };

  const sessionStore = useInjection<SessionStore>(ServiceTypes.SessionStore);

  const onSave = async () => {
    const rowsCpy = getRows().map((r) => r.clone()); // copy data
    getRows().forEach((r) => {
      r.isChanged = false;
    }); // assume update was successful
    rowManager.cellCacheManager.rerenderAllCells();
    rowManager.updateGrid();
    // we cannot update isChanged after save is completed, because tagger could change something while his previous changes are being saved
    const { changedIds, isSuccessful } = await reviewStore.savePlays(rowsCpy);
    if (!isSuccessful) {
      // if update failed, revert isChanged changes
      getRows().forEach((r) => {
        r.isChanged = r.isChanged || !!changedIds.includes(r.id);
      });
      rowManager.cellCacheManager.rerenderAllCells();
      rowManager.updateGrid();
    }
    await sessionStore.updateSessionMetadata(reviewStore.reviewGame.sessionMetadata, true);

    noteStore.setNote('');
    setSessionSettingsChanged(false);
  };

  const onSubmitClickHandler = async () => {
    if (!owningOrgId) {
      setMissingOrgModalVisible(true);

      return;
    }

    reviewStore.setIsSubmitModalVisible(true);
    noteStore.setNote('');
  };

  const recalculateAndSaveBeforeSubmit = async () => {
    rowManager.recalculateComputedProperties();
    rowManager.autogenerateMissingTimestamps(rowManager.rows);
    const rowsCpy = rowManager.rows.map((r) => r.clone());
    await reviewStore.savePlays(rowsCpy);
    await sessionStore.updateSessionMetadata(reviewStore.reviewGame.sessionMetadata, true);
  };

  const onSubmit = async (): Promise<void> => {
    await recalculateAndSaveBeforeSubmit();
    await reviewStore.submitReviewGame(id);
    reviewStore.setIsSubmitModalVisible(false);
    history.push('/archives');
  };

  const onSessionUpdatedHandler = (newSession: ISessionUpdateRequest) => {
    const oldSession = reviewStore.reviewGame.sessionMetadata;

    setOwningOrgId(newSession.owningOrganizationId);
    setSessionSettingsChanged(true);

    reviewStore.reviewGame.sessionMetadata = {
      ...reviewStore.reviewGame.sessionMetadata,
      ...({
        owningOrganizationId: newSession.owningOrganizationId,
        externalSessionId: newSession.externalSessionId,
      } as any),
    };

    if (oldSession.awayTeam?.id !== newSession.awayTeam.id || oldSession.homeTeam?.id !== newSession.homeTeam.id) {
      reviewStore.loadRosters(newSession.homeTeam.id, newSession.awayTeam.id);

      // Update temporary session metadata in the review store
      reviewStore.reviewGame.sessionMetadata = {
        ...reviewStore.reviewGame.sessionMetadata,
        ...({
          awayTeam: newSession.awayTeam,
          homeTeam: newSession.homeTeam,
          owningOrganizationId: newSession.owningOrganizationId,
          externalSessionId: newSession.externalSessionId,
        } as any),
      };

      rowManagerRef.current.handleTeamsChanged(
        newSession.homeTeam,
        newSession.awayTeam,
        oldSession.homeTeam?.id !== newSession.homeTeam.id,
        oldSession.awayTeam?.id !== newSession.awayTeam.id
      );
    }
  };

  return (
    <div>
      {shouldUpdate && reviewStore.isLoading && <Overlay hasSpinner />}
      <TopBar
        homeTeam={reviewStore.reviewGame.sessionMetadata.homeTeam}
        awayTeam={reviewStore.reviewGame.sessionMetadata.awayTeam}
        taggedDate={reviewStore.reviewGame.taggedOn}
        stadium={reviewStore.reviewGame.field}
        onSave={() => onSave()}
        areUnsavedChanges={getRows().some((r) => r.isChanged) || sessionSettingsChanged}
        isSaving={reviewStore.isSaving}
        onSubmitClickHandler={onSubmitClickHandler}
        isLoading={reviewStore.isLoading}
        shouldDisplayWarmups={isDisplayingWarmups}
        shouldDisplayWarmupsCallback={onToggleWarmupsChanged}
        shouldUpdate={shouldUpdate}
        gameRef={reviewStore.reviewGame.gameReference}
        sessionMetadata={reviewStore.reviewGame.sessionMetadata}
        onSessionUpdated={onSessionUpdatedHandler}
        rowManager={rowManagerRef.current}
        isSettingsModalVisible={isSettingsModalVisible}
        setSettingsModalVisible={setSettingsModalVisible}
      />
      <EditPlaysGrid session={reviewStore.reviewGame} rowManager={rowManagerRef.current} />
      <MissingOwningOrgModal
        isOpen={missingOrgModalVisible}
        onDismiss={() => setMissingOrgModalVisible(false)}
        submitWithoutOwningOrg={() => onSubmit()}
        openSettingsModal={() => {
          setMissingOrgModalVisible(false);
          setSettingsModalVisible(true);
        }}
      />
      <SubmitModal
        history={history}
        id={id}
        errorCount={
          getRows().length > 0
            ? getRows()
                .map((r) => r.status.errors.length)
                .reduce((acc, num) => acc + num)
            : 0
        }
        rowManager={rowManagerRef.current}
        onSubmit={onSubmit}
      />
    </div>
  );
});

export default SessionReview;
